/**
 * CertificationsTable
 *
 * Dense table for certifications; expiry warnings via Badge.
 * UnifyTable. Custom Badge for verification/expiry.
 */

import { Badge, Group, Stack, Text } from '@mantine/core';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { Certification } from '../types';
import { VerificationStatusLabels } from '../types';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getExpiryVariant(daysUntilExpiration: number | undefined): 'ok' | 'warning' | 'expired' | null {
  if (daysUntilExpiration === undefined) return null;
  if (daysUntilExpiration < 0) return 'expired';
  if (daysUntilExpiration <= 60) return 'warning';
  return 'ok';
}

const COLUMNS: UnifyTableColumn<Certification>[] = [
  {
    key: 'name',
    label: 'Certification',
    render: (c) => (
      <Text fw={500} size="sm">
        {c.name}
      </Text>
    ),
    getValue: (c) => c.name,
  },
  {
    key: 'issuingBody',
    label: 'Issuing body',
    render: (c) => <Text size="sm">{c.issuingBody}</Text>,
    getValue: (c) => c.issuingBody,
  },
  {
    key: 'issueDate',
    label: 'Issue date',
    render: (c) => <Text size="sm">{formatDate(c.issueDate)}</Text>,
    getValue: (c) => formatDate(c.issueDate),
  },
  {
    key: 'expiration',
    label: 'Expiration / Renewal',
    render: (c) => {
      const variant = getExpiryVariant(c.daysUntilExpiration);
      return (
        <Stack gap={4}>
          <Text size="sm">
            Exp: {formatDate(c.expirationDate)}
            {c.renewalDate && ` · Renew by ${formatDate(c.renewalDate)}`}
          </Text>
          {variant === 'warning' && (
            <Badge size="xs" color="yellow" variant="light">
              Expiring in {c.daysUntilExpiration} days
            </Badge>
          )}
          {variant === 'expired' && (
            <Badge size="xs" color="red" variant="filled">
              Expired
            </Badge>
          )}
        </Stack>
      );
    },
    getValue: (c) => formatDate(c.expirationDate),
  },
  {
    key: 'status',
    label: 'Status',
    render: (c) => (
      <Group gap="xs">
        <Badge size="xs" variant="light" color={c.verificationStatus === 'verified' ? 'green' : 'gray'}>
          {VerificationStatusLabels[c.verificationStatus]}
        </Badge>
        {c.attachmentUrl && (
          <Text size="xs" c="dimmed">
            Attachment on file
          </Text>
        )}
      </Group>
    ),
    getValue: (c) => VerificationStatusLabels[c.verificationStatus],
  },
];

interface CertificationsTableProps {
  certifications: Certification[];
}

export function CertificationsTable({ certifications }: CertificationsTableProps): JSX.Element {
  return (
    <UnifyTable withPaper={false} columns={COLUMNS} data={certifications} getRowKey={(c) => c.id} />
  );
}
